import type { DietaryInput, Invite, ResponseUpdate, RoomType } from './types'

/**
 * An error carrying the HTTP status, so callers can tell "your code is wrong"
 * (401) from "the server is unhappy" (5xx). The message is the one the API sent,
 * which is already written for guests to read.
 */
export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  /** True when there is no valid session, so the guest needs to enter a code. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

interface ErrorBody {
  error?: string
}

/**
 * Shared by the guest API below and the admin API in adminApi.ts. Exported so
 * the two speak to the server the same way - same credentials handling, same
 * error shape - rather than drifting apart.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      // The session lives in a cookie, so it has to ride along on every call.
      credentials: 'same-origin',
      headers: {
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    })
  } catch {
    // fetch only rejects for network-level problems, never for a 4xx/5xx.
    throw new ApiError(
      0,
      'We could not reach the server. Please check your connection and try again.',
    )
  }

  if (!res.ok) {
    let message = 'Something went wrong. Please try again.'
    try {
      const body = (await res.json()) as ErrorBody
      if (body.error) message = body.error
    } catch {
      // A non-JSON error body (a proxy or gateway, say) leaves the default.
    }
    throw new ApiError(res.status, message)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Exchanges an invite code for a session cookie and returns the invite. */
export function createSession(code: string): Promise<Invite> {
  return request<Invite>('/api/session', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

/** Signs the guest out. */
export function deleteSession(): Promise<void> {
  return request<void>('/api/session', { method: 'DELETE' })
}

/** Fetches the invite for the current session. Throws a 401 if there is none. */
export function getInvite(): Promise<Invite> {
  return request<Invite>('/api/invite')
}

/** Records responses. Only the people listed are changed. */
export function updateInvite(people: ResponseUpdate[]): Promise<Invite> {
  return request<Invite>('/api/invite', {
    method: 'PUT',
    body: JSON.stringify({ people }),
  })
}

/**
 * Records one guest's dietary requirements.
 *
 * Deliberately one person at a time, and separate from the RSVP: a household
 * replies to the invitation once, but the kitchen needs an answer for each
 * person. Returns the whole invite, so the caller does not need to refetch to
 * show the result.
 */
export function setDietary(personId: string, input: DietaryInput): Promise<Invite> {
  return request<Invite>(`/api/invite/people/${encodeURIComponent(personId)}/dietary`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

/** The rooms in Horsley Towers, with how many of each are left. */
export async function getRooms(): Promise<RoomType[]> {
  const body = await request<{ types: RoomType[] }>('/api/rooms')
  return body.types
}

/**
 * Holds a room for the whole invite, or moves it if it already holds one.
 *
 * Takes no invite id: the server acts on whoever is signed in, so one household
 * cannot book against another's. Returns the invite, carrying the reservation.
 */
export function reserveRoom(roomTypeId: string): Promise<Invite> {
  return request<Invite>('/api/invite/room', {
    method: 'PUT',
    body: JSON.stringify({ room_type_id: roomTypeId }),
  })
}

/** Gives up the room the invite holds. */
export function releaseRoom(): Promise<Invite> {
  return request<Invite>('/api/invite/room', { method: 'DELETE' })
}
